import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { formatUtcDateTime } from '../utils/timezone'
import type { ContactOption } from './ConversationContactPicker'
import '../style/components/CalendarEventPopover.css'

type CalendarEvent = {
  id: string
  title?: string
  datetime_start?: string
  datetime_end?: string
  summary?: string
  location?: string | null
  tags?: string[] | string | null
}

type CalendarEventPopoverProps = {
  event: CalendarEvent
  style?: CSSProperties
  tailLeft?: string
  tailPlacement?: 'top' | 'bottom'
  onSave?: (
    id: string,
    startIso: string,
    endIso: string,
    location?: string | null,
    tag?: string | null,
  ) => Promise<void> | void
  onArchive?: (id: string) => Promise<void> | void
  onDelete?: (id: string) => Promise<void> | void
  onCancel?: () => void
  onTitleChange?: (title: string) => void
  titleValue?: string
  locationValue?: string
  onLocationChange?: (location: string) => void
  clients?: ContactOption[]
  onAddClient?: () => void
  notificationEnabled?: boolean
  notificationOffsetMinutes?: number
  onNotificationChange?: (enabled: boolean, offsetMinutes: number) => void
}

const toLocalInput = (value?: string) => {
  const date = parseIncomingUtcDate(value)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return local.toISOString().slice(0, 16)
}

const parseIncomingUtcDate = (value?: string) => {
  if (!value) return new Date(Number.NaN)
  const normalized = value.includes('T') ? value : value.replace(' ', 'T')
  const hasExplicitZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized)
  const candidate = hasExplicitZone ? normalized : `${normalized}Z`
  return new Date(candidate)
}

const toIsoString = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

function CalendarEventPopover({
  event,
  style,
  tailLeft = '50%',
  tailPlacement = 'bottom',
  onSave,
  onArchive,
  onCancel,
  onTitleChange,
  titleValue,
  locationValue,
  onLocationChange,
  clients,
  onAddClient,
  notificationEnabled,
  notificationOffsetMinutes,
  onNotificationChange,
}: CalendarEventPopoverProps) {
  const initialStartInput = useMemo(() => toLocalInput(event.datetime_start), [event.datetime_start])
  const initialEndInput = useMemo(() => toLocalInput(event.datetime_end), [event.datetime_end])
  const [locationInput, setLocationInput] = useState(locationValue ?? event.location ?? '')
  const titleRef = useRef<HTMLTextAreaElement | null>(null)
  const [titleExpanded, setTitleExpanded] = useState(false)
  const [titleHeight, setTitleHeight] = useState<number | null>(null)
  const collapsedTitleHeight = 42

  const [startInput, setStartInput] = useState(initialStartInput)
  const [endInput, setEndInput] = useState(initialEndInput)
  const [durationMs, setDurationMs] = useState(() => {
    const start = parseIncomingUtcDate(event.datetime_start)
    const end = parseIncomingUtcDate(event.datetime_end)
    const delta = end.getTime() - start.getTime()
    return Number.isFinite(delta) && delta > 0 ? delta : 60 * 60 * 1000
  })
  const [saving, setSaving] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tagOpen, setTagOpen] = useState(false)
  const [tagDraft, setTagDraft] = useState('')
  const tagRef = useRef<HTMLDivElement | null>(null)

  const normalizedTag = useMemo(() => {
    if (Array.isArray(event.tags)) return event.tags[0] || ''
    return event.tags ?? ''
  }, [event.tags])
  const tagLabel = useMemo(() => {
    if (!tagDraft) return 'Default'
    if (tagDraft.toLowerCase() === 'azure' || tagDraft.toLowerCase() === 'outlook') return 'Outlook'
    return tagDraft[0].toUpperCase() + tagDraft.slice(1)
  }, [tagDraft])
  const tagClass = useMemo(() => {
    if (!tagDraft) return 'default'
    if (tagDraft.toLowerCase() === 'azure' || tagDraft.toLowerCase() === 'outlook') return 'azure'
    return tagDraft.toLowerCase()
  }, [tagDraft])
  const tagDisabled = useMemo(() => {
    if (!onSave) return true
    const lower = tagDraft.toLowerCase()
    return lower === 'azure' || lower === 'google' || lower === 'outlook'
  }, [onSave, tagDraft])

  useEffect(() => {
    setLocationInput(locationValue ?? event.location ?? '')
  }, [locationValue, event.location])

  useEffect(() => {
    const nextStart = toLocalInput(event.datetime_start)
    const nextEnd = toLocalInput(event.datetime_end)
    const startDate = parseIncomingUtcDate(event.datetime_start)
    const endDate = parseIncomingUtcDate(event.datetime_end)
    const nextDuration = endDate.getTime() - startDate.getTime()
    setStartInput(nextStart)
    setEndInput(nextEnd)
    setDurationMs(Number.isFinite(nextDuration) && nextDuration > 0 ? nextDuration : 60 * 60 * 1000)
  }, [event.id, event.datetime_start, event.datetime_end])

  useEffect(() => {
    setTagDraft(normalizedTag)
    setTagOpen(false)
  }, [event.id, normalizedTag])

  useEffect(() => {
    if (!titleExpanded) return
    const el = titleRef.current
    if (!el) return
    el.style.height = '0px'
    const nextHeight = Math.max(el.scrollHeight, 56)
    el.style.height = `${nextHeight}px`
    setTitleHeight(nextHeight)
  }, [titleExpanded, titleValue, event.title])

  useEffect(() => {
    if (!tagOpen) return
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (tagRef.current?.contains(target)) return
      setTagOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [tagOpen])

  const displayStartIso = toIsoString(startInput) || event.datetime_start
  const displayEndIso = toIsoString(endInput) || event.datetime_end
  const startDisplay = formatUtcDateTime(displayStartIso)
  const endDisplay = formatUtcDateTime(displayEndIso)

  const handleStartChange = (value: string) => {
    setStartInput(value)
    const startDate = new Date(value)
    if (Number.isNaN(startDate.getTime())) return
    const newEnd = new Date(startDate.getTime() + durationMs)
    const local = new Date(newEnd.getTime() - newEnd.getTimezoneOffset() * 60000)
    setEndInput(local.toISOString().slice(0, 16))
  }

  const handleEndChange = (value: string) => {
    setEndInput(value)
    const startDate = new Date(startInput)
    const endDate = new Date(value)
    if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
      const delta = endDate.getTime() - startDate.getTime()
      if (delta > 0) setDurationMs(delta)
    }
  }

  const handleCancel = () => {
    setStartInput(initialStartInput)
    setEndInput(initialEndInput)
    setLocationInput(locationValue ?? event.location ?? '')
    setTagDraft(normalizedTag)
    setError(null)
  }

  const handleSave = async () => {
    if (!onSave) return
    const startIso = toIsoString(startInput)
    const endIso = toIsoString(endInput)
    if (!startIso || !endIso) {
      setError('Please enter valid start and end times.')
      return
    }
    if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      setError('End time must be after start time.')
      return
    }
    try {
      setSaving(true)
      setError(null)
      await onSave(event.id, startIso, endIso, locationInput.trim() || null, tagDraft.trim() || null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event.')
    } finally {
      setSaving(false)
    }
  }

  const handleArchive = async () => {
    if (!onArchive) return
    try {
      setArchiving(true)
      setError(null)
      await onArchive(event.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive event.')
    } finally {
      setArchiving(false)
    }
  }

  return (
    <div
      className="modal-card"
      role="dialog"
      aria-modal="true"
      aria-labelledby="event-title"
      data-tail={tailPlacement}
      style={{
        ...style,
        ['--tail-left' as string]: tailLeft,
      }}
    >
      <div className="modal-header" style={{ alignItems: 'flex-start', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <textarea
            ref={titleRef}
            value={titleValue ?? event.title ?? ''}
            onChange={(e) => onTitleChange?.(e.target.value)}
            placeholder="Event title"
            rows={1}
            onFocus={() => setTitleExpanded(true)}
            onBlur={() => setTitleExpanded(false)}
            style={{
              width: '100%',
              fontSize: '20px',
              fontWeight: 800,
              fontFamily: 'inherit',
              border: '1px solid #dfe4f1',
              borderRadius: '10px',
              padding: '8px 10px',
              minHeight: `${collapsedTitleHeight}px`,
              lineHeight: 1.25,
              resize: 'none',
              overflow: 'hidden',
              height: titleExpanded ? `${titleHeight ?? collapsedTitleHeight}px` : `${collapsedTitleHeight}px`,
            }}
            className="popover-title-textarea"
          />
        </div>
        <div style={{ textAlign: 'right', minWidth: '200px' }}>
          <div className="modal-date">{startDisplay.date}</div>
          <div className="modal-time">
            {startDisplay.time} – {endDisplay.time || '—'}
          </div>
        </div>
      </div>
      <div className="modal-body">
        <div className="form-grid" style={{ marginTop: 10 }}>
          <label>
            <span>Start</span>
            <input
              type="datetime-local"
              value={startInput}
              onChange={(e) => handleStartChange(e.target.value)}
            />
          </label>
          <label>
            <span>End</span>
            <input
              type="datetime-local"
              value={endInput}
              onChange={(e) => handleEndChange(e.target.value)}
            />
          </label>
          <label>
            <span>Location</span>
            <input
              type="text"
              value={locationInput}
              onChange={(e) => {
                setLocationInput(e.target.value)
                onLocationChange?.(e.target.value)
              }}
              placeholder="Add a place or link"
            />
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Attendees</span>
              {onAddClient && (
                <button type="button" className="icon-button signal" style={{ width: 26, height: 26, padding: 0 }} onClick={onAddClient} title="Add contact">
                  +
                </button>
              )}
            </div>
            <div
              style={{
                height: 40,
                overflowY: 'auto',
                border: '1px solid #dfe4f1',
                borderRadius: 10,
                padding: '6px 8px',
                display: 'flex',
                gap: 6,
                alignItems: 'center',
                flexWrap: 'wrap',
                background: '#fff',
              }}
            >
              {clients && clients.length > 0 ? (
                clients.map((c) => (
                  <span
                    key={c.id}
                    className="pill muted"
                    style={{
                      padding: '3px 8px',
                      border: '1px solid #dfe4f1',
                      borderRadius: 999,
                      fontSize: 12,
                    }}
                  >
                    {c.fullName || [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Contact'}
                  </span>
                ))
              ) : (
                <span className="muted" style={{ fontSize: 12 }}>No contacts linked.</span>
              )}
            </div>
          </label>
        </div>
        {onNotificationChange && (
          <div className="notification-toggle" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notificationEnabled ?? true}
                onChange={(e) => onNotificationChange(e.target.checked, notificationOffsetMinutes ?? 60)}
                style={{ accentColor: '#6481d2' }}
              />
              SMS reminder
            </label>
            {(notificationEnabled ?? true) && (
              <select
                value={notificationOffsetMinutes ?? 60}
                onChange={(e) => onNotificationChange(notificationEnabled ?? true, Number(e.target.value))}
                style={{ fontSize: 12, padding: '2px 6px', borderRadius: 6, border: '1px solid #dfe4f1' }}
              >
                <option value={15}>15 min before</option>
                <option value={30}>30 min before</option>
                <option value={60}>1 hour before</option>
                <option value={120}>2 hours before</option>
              </select>
            )}
          </div>
        )}
        {event.summary && <p className="modal-summary" style={{ marginTop: 10 }}>{event.summary}</p>}
        {error && (
          <div className="muted" style={{ color: '#b42318', marginTop: 6 }}>
            {error}
          </div>
        )}
      </div>
      <div className="modal-footer">
        <div className="modal-tag" ref={tagRef}>
          <span className="tag-eyebrow">Tag</span>
          <button
            type="button"
            className={`tag-pill ${tagClass}`}
            onClick={() => {
              if (tagDisabled) return
              setTagOpen((prev) => !prev)
            }}
            disabled={tagDisabled}
          >
            {tagLabel}
          </button>
          {tagOpen && (
            <div className="tag-popover">
              <div className="tag-pill-list" role="listbox" aria-label="Tag options">
                {[
                  { value: '', label: 'Default', className: 'default' },
                  { value: 'reminder', label: 'Reminder', className: 'reminder' },
                  { value: 'meeting', label: 'Meeting', className: 'meeting' },
                ].map((option) => (
                  <button
                    type="button"
                    key={option.value || 'default'}
                    role="option"
                    aria-selected={tagDraft === option.value}
                    className={`tag-pill ${option.className} ${
                      tagDraft === option.value ? 'selected' : ''
                    }`}
                    onClick={() => {
                      setTagDraft(option.value)
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-actions">
          {onArchive && (
            <button
              type="button"
              className="icon-button danger with-label archive-button"
              onClick={handleArchive}
              disabled={archiving || saving}
            >
              {archiving ? 'Archiving…' : '🗑️'}
            </button>
          )}
          <button
            type="button"
            className="ghost-button"
            onClick={() => {
              handleCancel()
              onCancel?.()
            }}
            disabled={saving || archiving}
          >
            Cancel
          </button>
          <button
            className="primary-button"
            onClick={handleSave}
            disabled={saving || !onSave || archiving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CalendarEventPopover
